import { Controller } from '@hotwired/stimulus'
import { googleFontStack } from '../embed/google_font'

// Sticky order footer page: rebuilds the sticky footer JSON from the unsaved form and draws it with the real
// renderer (embed/footer.js) inside the stage. Keep in step with app/models/order_footer_theme.rb.
const FONTS = {
  noto: '"Noto Sans", sans-serif',
  inherit: 'inherit',
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif'
}

function fontStack(theme) {
  if (theme.font !== 'google') return FONTS[theme.font] || FONTS.noto

  return googleFontStack(theme.font_family, theme.font_category)
}

export function variables(theme) {
  const solid = theme.button_style !== 'outline'
  return {
    '--of-bg': solid ? theme.background : 'transparent',
    '--of-fg': solid ? theme.text : theme.background,
    '--of-border': theme.background,
    '--of-radius': theme.button_radius === 'pill' ? '50px' : `${theme.button_radius}px`,
    '--of-font': fontStack(theme)
  }
}

export default class extends Controller {
  static targets = ['stage', 'base']

  connect() {
    this.base = JSON.parse(this.baseTarget.textContent)
    if (window.EightyEight) this.render()
    else document.addEventListener('eightyeight:ready', () => this.render(), { once: true })
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    if (this.preview) this.preview.destroy()
  }

  // Click too: the segmented controls change hidden inputs, which fire no input event.
  refresh() {
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.render())
  }

  render() {
    if (!window.EightyEight || !window.EightyEight.previewFooter) return
    if (this.preview) this.preview.destroy()
    this.preview = window.EightyEight.previewFooter(this.stageTarget, this.footer())
  }

  footer() {
    const theme = this.theme()
    const custom = theme.mode === 'custom'
    return {
      mode: custom ? 'custom' : 'ordering',
      label: custom ? theme.label || 'Your button' : this.base.ordering_label,
      url: custom ? theme.url : this.base.order_url,
      order_now: custom ? false : this.base.order_now,
      css_variables: variables(theme),
      font_family: theme.font === 'google' ? theme.font_family : '',
      rules: {
        placement: theme.placement,
        size: theme.size,
        hide_on_mobile: theme.hide_on_mobile === '1',
        pages: theme.pages,
        paths: (theme.paths || '').split('\n').map((line) => line.trim()).filter(Boolean),
        dismissible: theme.dismissible === '1',
        reveal: theme.reveal,
        reveal_seconds: Number(theme.reveal_seconds),
        reveal_percent: Number(theme.reveal_percent)
      }
    }
  }

  // Field order in FormData puts a checkbox's own hidden "0" companion before the checkbox itself, so
  // the last value under a name wins: '1' when checked, '0' when not.
  theme() {
    const form = this.element.querySelector('form')
    const theme = {}
    if (!form) return theme
    for (const [name, value] of new FormData(form).entries()) {
      const match = name.match(/^order_footer_theme\[(\w+)\]$/)
      if (match) theme[match[1]] = value
    }
    return theme
  }
}
